import { trackerDefinitionSchema } from './schema.js';
/**
 * Valida y normaliza una plantilla al cargar el módulo.
 *
 * Que esto corra al importar es la gracia: una plantilla malformada rompe el
 * arranque de la API, no la pantalla de un paciente cargando sus síntomas.
 */
function define(input) {
    const result = trackerDefinitionSchema.safeParse(input);
    if (!result.success) {
        throw new Error(`Plantilla de tracker inválida "${input.key}": ${JSON.stringify(result.error.issues, null, 2)}`);
    }
    return result.data;
}
/**
 * Biblioteca clínica de Trellis.
 *
 * Son las plantillas que Dx Tech mantiene y que cada pharma clona para armar su
 * programa. Una plantilla no se edita en producción: la pharma se lleva una copia
 * a su `TenantTracker` y modifica esa. Así una corrección acá no altera programas
 * que ya están corriendo con pacientes cargando datos.
 *
 * Sobre licencias — importa para vender:
 *   · ESAS-r, PHQ-9, GAD-7 y el termómetro de distress del NCCN son de uso libre.
 *   · PRO-CTCAE es del NCI, de uso libre.
 *   · EORTC QLQ-C30 y FACT-G NO están acá: requieren licencia paga para uso
 *     comercial de una institución. El motor los soporta, pero no se distribuyen
 *     hasta que exista la licencia.
 */
// ── Escalas de respuesta reutilizables ───────────────────────────────────────
/** Frecuencia estándar de PHQ-9 y GAD-7, sobre las últimas 2 semanas. */
const FREQ_0_3 = [
    { value: 'never', label: 'Ningún día', score: 0 },
    { value: 'several', label: 'Varios días', score: 1 },
    { value: 'more_than_half', label: 'Más de la mitad de los días', score: 2 },
    { value: 'nearly_every_day', label: 'Casi todos los días', score: 3 },
];
/** Severidad estándar de PRO-CTCAE. */
const PROCTCAE_SEVERITY = [
    { value: 'none', label: 'Ninguna', score: 0 },
    { value: 'mild', label: 'Leve', score: 1 },
    { value: 'moderate', label: 'Moderada', score: 2 },
    { value: 'severe', label: 'Severa', score: 3 },
    { value: 'very_severe', label: 'Muy severa', score: 4 },
];
/** Interferencia con las actividades diarias, escala estándar de PRO-CTCAE. */
const PROCTCAE_INTERFERENCE = [
    { value: 'not_at_all', label: 'Nada', score: 0 },
    { value: 'a_little', label: 'Un poco', score: 1 },
    { value: 'somewhat', label: 'Algo', score: 2 },
    { value: 'quite_a_bit', label: 'Bastante', score: 3 },
    { value: 'very_much', label: 'Muchísimo', score: 4 },
];
/** Atajo para los 9 ítems 0-10 del ESAS-r. */
function esasItem(key, label, minAnchor, maxAnchor) {
    return {
        key,
        label,
        type: 'scale',
        required: true,
        min: 0,
        max: 10,
        step: 1,
        anchors: { min: minAnchor, max: maxAnchor },
    };
}
function likert(key, label, options, required = true) {
    return { key, label, type: 'choice', required, options };
}
// ── SÍNTOMAS ─────────────────────────────────────────────────────────────────
export const esasR = define({
    key: 'esas_r',
    name: 'Escala de síntomas ESAS-r',
    shortName: 'Síntomas',
    description: 'Nueve síntomas puntuados de 0 a 10 según cómo te sentiste hoy. Se completa en un minuto.',
    icon: 'thermometer',
    category: 'SYMPTOMS',
    instrument: {
        name: 'Edmonton Symptom Assessment System — revised (ESAS-r)',
        source: 'Regional Palliative Care Program, Edmonton',
        license: 'public-domain',
        notes: 'Estándar en oncología. Validado al español. Uso libre.',
    },
    frequency: 'DAILY',
    reminderTime: '19:00',
    sections: [
        {
            key: 'physical',
            title: 'Síntomas físicos',
            description: 'Marcá el número que mejor describe cómo te sentiste hoy.',
            fields: [
                esasItem('pain', 'Dolor', 'Sin dolor', 'El peor dolor posible'),
                esasItem('tiredness', 'Cansancio (falta de energía)', 'Sin cansancio', 'El peor cansancio posible'),
                esasItem('drowsiness', 'Somnolencia (sentirse adormecido)', 'Sin somnolencia', 'La peor somnolencia posible'),
                esasItem('nausea', 'Náuseas', 'Sin náuseas', 'Las peores náuseas posibles'),
                esasItem('appetite', 'Falta de apetito', 'Sin falta de apetito', 'La peor falta de apetito posible'),
                esasItem('dyspnea', 'Falta de aire', 'Sin falta de aire', 'La peor falta de aire posible'),
            ],
        },
        {
            key: 'emotional',
            title: 'Estado emocional',
            fields: [
                esasItem('depression', 'Depresión (sentirse triste)', 'Sin depresión', 'La peor depresión posible'),
                esasItem('anxiety', 'Ansiedad (sentirse nervioso)', 'Sin ansiedad', 'La peor ansiedad posible'),
            ],
        },
        {
            key: 'global',
            title: 'Bienestar general',
            fields: [
                // Ojo con la dirección: en el ESAS-r, 0 es el MEJOR bienestar. Invertirlo
                // rompe la comparabilidad con la literatura y con la propia serie histórica.
                esasItem('wellbeing', 'Bienestar general', 'Mejor bienestar', 'Peor bienestar'),
            ],
        },
    ],
    scores: [
        {
            key: 'esas_total',
            label: 'Carga sintomática total',
            method: 'sum',
            fields: [
                'pain',
                'tiredness',
                'drowsiness',
                'nausea',
                'appetite',
                'dyspnea',
                'depression',
                'anxiety',
                'wellbeing',
            ],
            bands: [
                { upTo: 17, label: 'Carga leve', severity: 'INFO' },
                { upTo: 35, label: 'Carga moderada', severity: 'MEDIUM' },
                { upTo: 90, label: 'Carga alta', severity: 'HIGH' },
            ],
        },
        {
            key: 'esas_worst',
            label: 'Síntoma más intenso',
            method: 'max',
            fields: ['pain', 'tiredness', 'drowsiness', 'nausea', 'appetite', 'dyspnea', 'depression', 'anxiety'],
            bands: [
                { upTo: 3, label: 'Leve', severity: 'INFO' },
                { upTo: 6, label: 'Moderado', severity: 'MEDIUM' },
                { upTo: 10, label: 'Severo', severity: 'HIGH' },
            ],
        },
    ],
    chartMetrics: ['esas_total', 'pain', 'tiredness', 'nausea'],
    completionMessage: 'Gracias. Tu equipo de seguimiento puede ver esta información.',
});
export const proCtcae = define({
    key: 'pro_ctcae',
    name: 'Eventos adversos reportados por el paciente',
    shortName: 'Efectos del tratamiento',
    description: 'Sobre los últimos 7 días. Ayuda a tu equipo a anticipar efectos del tratamiento.',
    icon: 'clipboard-list',
    category: 'SYMPTOMS',
    instrument: {
        name: 'PRO-CTCAE (subset para inmunoterapia)',
        source: 'National Cancer Institute (NCI)',
        license: 'public-domain',
        notes: 'Subconjunto de ítems relevantes para inmunoterapia. La pharma ajusta qué ítems quedan según el fármaco de su programa.',
    },
    frequency: 'WEEKLY',
    sections: [
        {
            key: 'general',
            title: 'Síntomas generales',
            description: 'En los últimos 7 días…',
            fields: [
                likert('fatigue_severity', '¿Cuál fue la severidad de tu cansancio o fatiga en su peor momento?', PROCTCAE_SEVERITY),
                likert('fatigue_interference', '¿Cuánto interfirió el cansancio con tus actividades diarias?', PROCTCAE_INTERFERENCE),
                likert('appetite_severity', '¿Cuál fue la severidad de tu falta de apetito en su peor momento?', PROCTCAE_SEVERITY),
            ],
        },
        {
            key: 'gastro',
            title: 'Digestivo',
            fields: [
                likert('nausea_severity', '¿Cuál fue la severidad de tus náuseas en su peor momento?', PROCTCAE_SEVERITY),
                {
                    key: 'diarrhea_frequency',
                    label: '¿Cuántas deposiciones líquidas tuviste por día, como máximo?',
                    type: 'choice',
                    required: true,
                    // La diarrea es el evento adverso inmunomediado que más rápido escala
                    // en inmunoterapia: una colitis puede volverse grave en días. Por eso
                    // se pregunta por conteo y no por severidad percibida.
                    options: [
                        { value: 'none', label: 'Ninguna', score: 0 },
                        { value: 'one_to_three', label: '1 a 3 por día', score: 1 },
                        { value: 'four_to_six', label: '4 a 6 por día', score: 2 },
                        { value: 'seven_or_more', label: '7 o más por día', score: 3 },
                    ],
                },
                likert('mouth_sores', '¿Cuál fue la severidad de las llagas en la boca?', PROCTCAE_SEVERITY, false),
            ],
        },
        {
            key: 'derm',
            title: 'Piel',
            fields: [
                likert('rash_severity', '¿Cuál fue la severidad del sarpullido o erupción en la piel?', PROCTCAE_SEVERITY, false),
                likert('itching_severity', '¿Cuál fue la severidad de la picazón?', PROCTCAE_SEVERITY, false),
            ],
        },
        {
            key: 'resp',
            title: 'Respiratorio',
            fields: [
                likert('dyspnea_severity', '¿Cuál fue la severidad de tu falta de aire en su peor momento?', PROCTCAE_SEVERITY),
                likert('cough_severity', '¿Cuál fue la severidad de la tos?', PROCTCAE_SEVERITY, false),
            ],
        },
        {
            key: 'neuro',
            title: 'Neurológico',
            fields: [
                likert('neuropathy_severity', '¿Cuál fue la severidad del hormigueo o adormecimiento en manos o pies?', PROCTCAE_SEVERITY, false),
                likert('joint_pain', '¿Cuál fue la severidad del dolor en las articulaciones?', PROCTCAE_SEVERITY, false),
            ],
        },
    ],
    scores: [
        {
            key: 'proctcae_max',
            label: 'Grado máximo reportado',
            method: 'max',
            fields: [
                'fatigue_severity',
                'appetite_severity',
                'nausea_severity',
                'mouth_sores',
                'rash_severity',
                'itching_severity',
                'dyspnea_severity',
                'cough_severity',
                'neuropathy_severity',
                'joint_pain',
            ],
            bands: [
                { upTo: 1, label: 'Leve', severity: 'INFO' },
                { upTo: 2, label: 'Moderado', severity: 'MEDIUM' },
                { upTo: 4, label: 'Severo', severity: 'HIGH' },
            ],
        },
    ],
    chartMetrics: ['proctcae_max', 'fatigue_severity', 'diarrhea_frequency'],
});
export const vitals = define({
    key: 'vitals',
    name: 'Signos vitales',
    shortName: 'Temperatura',
    description: 'Sobre todo la temperatura: durante el tratamiento, la fiebre se controla el mismo día.',
    icon: 'thermometer-sun',
    category: 'SYMPTOMS',
    frequency: 'ON_DEMAND',
    sections: [
        {
            key: 'vitals',
            fields: [
                {
                    key: 'temperature',
                    label: 'Temperatura',
                    type: 'number',
                    required: true,
                    unit: '°C',
                    min: 33,
                    max: 43,
                    step: 0.1,
                    // 38 °C es el umbral de neutropenia febril, que es una urgencia
                    // oncológica: requiere antibiótico dentro de la hora.
                    help: 'Si tenés 38 °C o más, contactá a tu equipo hoy mismo.',
                },
                {
                    key: 'chills',
                    label: '¿Tenés escalofríos?',
                    type: 'boolean',
                },
                {
                    key: 'heart_rate',
                    label: 'Frecuencia cardíaca',
                    type: 'number',
                    unit: 'lpm',
                    min: 30,
                    max: 220,
                    step: 1,
                },
                {
                    key: 'systolic',
                    label: 'Presión sistólica (la alta)',
                    type: 'number',
                    unit: 'mmHg',
                    min: 50,
                    max: 260,
                    step: 1,
                },
                {
                    key: 'diastolic',
                    label: 'Presión diastólica (la baja)',
                    type: 'number',
                    unit: 'mmHg',
                    min: 30,
                    max: 160,
                    step: 1,
                },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['temperature'],
});
export const painDiary = define({
    key: 'pain_diary',
    name: 'Diario de dolor',
    shortName: 'Dolor',
    description: 'Registrá dónde te duele, cuánto, y si necesitaste medicación de rescate.',
    icon: 'activity',
    category: 'SYMPTOMS',
    frequency: 'ON_DEMAND',
    sections: [
        {
            key: 'pain',
            fields: [
                {
                    key: 'intensity',
                    label: 'Intensidad del dolor ahora',
                    type: 'scale',
                    required: true,
                    min: 0,
                    max: 10,
                    step: 1,
                    anchors: { min: 'Sin dolor', max: 'El peor dolor imaginable' },
                },
                {
                    key: 'locations',
                    label: '¿Dónde te duele?',
                    type: 'bodymap',
                    help: 'Podés marcar más de una zona.',
                },
                {
                    key: 'quality',
                    label: '¿Cómo es el dolor?',
                    type: 'multichoice',
                    options: [
                        { value: 'sharp', label: 'Punzante' },
                        { value: 'burning', label: 'Ardor o quemazón' },
                        { value: 'throbbing', label: 'Pulsátil' },
                        { value: 'cramping', label: 'Calambre' },
                        { value: 'tingling', label: 'Hormigueo' },
                        { value: 'dull', label: 'Sordo o constante' },
                    ],
                },
                {
                    key: 'rescue_taken',
                    label: '¿Tomaste medicación de rescate?',
                    type: 'boolean',
                },
                {
                    key: 'rescue_relief',
                    label: '¿Cuánto te alivió?',
                    type: 'scale',
                    min: 0,
                    max: 10,
                    step: 1,
                    anchors: { min: 'Nada', max: 'Alivio total' },
                    showIf: { field: 'rescue_taken', equals: true },
                },
                { key: 'notes', label: 'Notas', type: 'text', placeholder: 'Algo que quieras contarle a tu equipo' },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['intensity'],
});
// ── NUTRICIÓN ────────────────────────────────────────────────────────────────
export const weight = define({
    key: 'weight',
    name: 'Peso corporal',
    shortName: 'Peso',
    description: 'Pesate siempre a la misma hora, idealmente en ayunas y con poca ropa.',
    icon: 'scale',
    category: 'NUTRITION',
    frequency: 'WEEKLY',
    reminderTime: '08:00',
    sections: [
        {
            key: 'weight',
            fields: [
                { key: 'weight_kg', label: 'Peso', type: 'number', required: true, unit: 'kg', min: 25, max: 300, step: 0.1 },
                {
                    key: 'arm_circumference',
                    label: 'Circunferencia del brazo',
                    type: 'number',
                    unit: 'cm',
                    min: 10,
                    max: 60,
                    step: 0.5,
                    // La circunferencia braquial detecta pérdida de masa muscular incluso
                    // cuando el peso se mantiene por retención de líquidos, que es
                    // justamente cuando el peso solo engaña.
                    help: 'Opcional. Medí en el punto medio entre el hombro y el codo, con el brazo relajado.',
                },
                {
                    key: 'appetite_today',
                    label: '¿Cómo estuvo tu apetito esta semana?',
                    type: 'choice',
                    options: [
                        { value: 'good', label: 'Bueno, como siempre', score: 0 },
                        { value: 'reduced', label: 'Algo reducido', score: 1 },
                        { value: 'poor', label: 'Malo, como poco', score: 2 },
                        { value: 'very_poor', label: 'Muy malo, casi no como', score: 3 },
                    ],
                },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['weight_kg', 'arm_circumference'],
});
export const mealLog = define({
    key: 'meal_log',
    name: 'Registro de alimentación',
    shortName: 'Alimentación',
    description: 'Cómo venís comiendo y qué te está costando.',
    icon: 'utensils',
    category: 'NUTRITION',
    frequency: 'DAILY',
    reminderTime: '21:00',
    sections: [
        {
            key: 'intake',
            title: 'Lo que comiste hoy',
            fields: [
                {
                    key: 'meals_count',
                    label: '¿Cuántas comidas hiciste?',
                    type: 'number',
                    required: true,
                    min: 0,
                    max: 10,
                    step: 1,
                },
                {
                    key: 'portion',
                    label: '¿Cuánto lograste comer, en general?',
                    type: 'choice',
                    required: true,
                    options: [
                        { value: 'full', label: 'La porción completa', score: 0 },
                        { value: 'most', label: 'Más de la mitad', score: 1 },
                        { value: 'half', label: 'Alrededor de la mitad', score: 2 },
                        { value: 'few_bites', label: 'Unos pocos bocados', score: 3 },
                        { value: 'nothing', label: 'Casi nada', score: 4 },
                    ],
                },
                {
                    key: 'protein_servings',
                    label: 'Porciones de proteína (carne, huevo, legumbres, lácteos)',
                    type: 'number',
                    min: 0,
                    max: 12,
                    step: 1,
                    // La ingesta proteica es lo primero que cae y lo que más pesa en la
                    // pérdida de masa muscular durante el tratamiento.
                    help: 'Es lo que más ayuda a conservar masa muscular durante el tratamiento.',
                },
            ],
        },
        {
            key: 'barriers',
            title: '¿Algo te dificultó comer?',
            fields: [
                {
                    key: 'barriers',
                    label: 'Marcá lo que te pasó hoy',
                    type: 'multichoice',
                    options: [
                        { value: 'nausea', label: 'Náuseas' },
                        { value: 'vomiting', label: 'Vómitos' },
                        { value: 'mucositis', label: 'Llagas o dolor en la boca' },
                        { value: 'dysgeusia', label: 'La comida me sabe raro o a metal' },
                        { value: 'dry_mouth', label: 'Boca seca' },
                        { value: 'early_satiety', label: 'Me lleno enseguida' },
                        { value: 'dysphagia', label: 'Me cuesta tragar' },
                        { value: 'smell_aversion', label: 'Los olores me molestan' },
                        { value: 'constipation', label: 'Constipación' },
                        { value: 'diarrhea', label: 'Diarrea' },
                        { value: 'no_appetite', label: 'Sin ganas de comer' },
                    ],
                },
                { key: 'notes', label: 'Notas', type: 'text' },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['meals_count', 'portion', 'barriers_count'],
});
export const hydration = define({
    key: 'hydration',
    name: 'Hidratación',
    shortName: 'Hidratación',
    description: 'Tomar suficiente líquido ayuda a tolerar mejor el tratamiento.',
    icon: 'droplets',
    category: 'NUTRITION',
    frequency: 'DAILY',
    sections: [
        {
            key: 'hydration',
            fields: [
                {
                    key: 'glasses',
                    label: 'Vasos de líquido que tomaste hoy',
                    type: 'number',
                    required: true,
                    min: 0,
                    max: 30,
                    step: 1,
                    unit: 'vasos',
                    help: 'Un vaso son unos 250 ml. Cuenta el agua, el caldo, el té y los jugos.',
                },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['glasses'],
});
// ── TRATAMIENTO ──────────────────────────────────────────────────────────────
export const medAdherence = define({
    key: 'med_adherence',
    name: 'Toma de medicación',
    shortName: 'Medicación',
    description: 'Confirmá si tomaste tu medicación. Si te la salteaste, contanos por qué.',
    icon: 'pill',
    category: 'TREATMENT',
    frequency: 'DAILY',
    reminderTime: '09:00',
    sections: [
        {
            key: 'adherence',
            fields: [
                { key: 'taken', label: '¿Tomaste tu medicación?', type: 'boolean', required: true },
                {
                    key: 'missed_reason',
                    label: '¿Por qué no la tomaste?',
                    type: 'choice',
                    showIf: { field: 'taken', equals: false },
                    // Saber el motivo es lo que hace accionable la adherencia: "me olvidé"
                    // se resuelve con un recordatorio, "me cayó mal" necesita al médico, y
                    // "no la pude conseguir" es un problema de acceso que la pharma puede
                    // resolver ella misma.
                    options: [
                        { value: 'forgot', label: 'Me olvidé' },
                        { value: 'side_effects', label: 'Me cayó mal / efectos secundarios' },
                        { value: 'ran_out', label: 'Se me terminó' },
                        { value: 'could_not_get', label: 'No la pude conseguir' },
                        { value: 'felt_better', label: 'Me sentía bien y no la tomé' },
                        { value: 'doctor_said', label: 'Mi médico me indicó suspenderla' },
                        { value: 'other', label: 'Otro motivo' },
                    ],
                },
                { key: 'notes', label: 'Notas', type: 'text', showIf: { field: 'taken', equals: false } },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['taken'],
});
export const labValues = define({
    key: 'lab_values',
    name: 'Resultados de institución',
    shortName: 'Institución',
    description: 'Cargá los valores de tu último análisis para que tu equipo los tenga a mano.',
    icon: 'flask-conical',
    category: 'TREATMENT',
    frequency: 'ON_DEMAND',
    sections: [
        {
            key: 'hematology',
            title: 'Hemograma',
            fields: [
                { key: 'lab_date', label: 'Fecha del análisis', type: 'date', required: true },
                { key: 'hemoglobin', label: 'Hemoglobina', type: 'number', unit: 'g/dL', min: 2, max: 25, step: 0.1 },
                {
                    key: 'neutrophils',
                    label: 'Neutrófilos absolutos',
                    type: 'number',
                    unit: '/mm³',
                    min: 0,
                    max: 30000,
                    step: 10,
                    // Por debajo de 500 /mm³ hay neutropenia severa: el riesgo de infección
                    // grave es alto y cambia la conducta del equipo tratante.
                    help: 'Por debajo de 500 el riesgo de infección es alto.',
                },
                { key: 'platelets', label: 'Plaquetas', type: 'number', unit: '/mm³', min: 0, max: 1000000, step: 1000 },
                { key: 'leukocytes', label: 'Glóbulos blancos', type: 'number', unit: '/mm³', min: 0, max: 100000, step: 100 },
            ],
        },
        {
            key: 'chemistry',
            title: 'Química',
            fields: [
                { key: 'creatinine', label: 'Creatinina', type: 'number', unit: 'mg/dL', min: 0, max: 20, step: 0.01 },
                { key: 'alt', label: 'ALT / TGP', type: 'number', unit: 'U/L', min: 0, max: 2000, step: 1 },
                { key: 'ast', label: 'AST / TGO', type: 'number', unit: 'U/L', min: 0, max: 2000, step: 1 },
                { key: 'tsh', label: 'TSH', type: 'number', unit: 'µUI/mL', min: 0, max: 100, step: 0.01 },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['hemoglobin', 'neutrophils', 'platelets'],
});
// ── BIENESTAR ────────────────────────────────────────────────────────────────
export const phq9 = define({
    key: 'phq9',
    name: 'Cuestionario de estado de ánimo (PHQ-9)',
    shortName: 'Ánimo',
    description: 'Durante las últimas 2 semanas, ¿con qué frecuencia te molestaron estos problemas?',
    icon: 'heart-pulse',
    category: 'WELLBEING',
    instrument: {
        name: 'Patient Health Questionnaire-9 (PHQ-9)',
        source: 'Spitzer, Kroenke & Williams — Pfizer',
        license: 'public-domain',
        notes: 'Uso libre, sin necesidad de permiso. Validado al español.',
    },
    frequency: 'MONTHLY',
    sections: [
        {
            key: 'phq9',
            fields: [
                likert('q1', 'Poco interés o placer en hacer cosas', FREQ_0_3),
                likert('q2', 'Sentirte decaído, deprimido o sin esperanzas', FREQ_0_3),
                likert('q3', 'Problemas para dormir, o dormir demasiado', FREQ_0_3),
                likert('q4', 'Sentirte cansado o con poca energía', FREQ_0_3),
                likert('q5', 'Poco apetito o comer en exceso', FREQ_0_3),
                likert('q6', 'Sentirte mal contigo mismo, o que sos un fracaso, o que te fallaste a vos o a tu familia', FREQ_0_3),
                likert('q7', 'Dificultad para concentrarte en cosas como leer o mirar televisión', FREQ_0_3),
                likert('q8', 'Moverte o hablar tan lento que otros lo notaron; o al revés, estar tan inquieto que te movías mucho más de lo habitual', FREQ_0_3),
                // El ítem 9 es el que importa por sobre el puntaje total: cualquier
                // respuesta distinta de "ningún día" exige contacto, sin esperar a que
                // el total cruce un umbral.
                likert('q9', 'Pensamientos de que estarías mejor muerto, o de lastimarte de alguna manera', FREQ_0_3),
            ],
        },
    ],
    scores: [
        {
            key: 'phq9_total',
            label: 'Puntaje PHQ-9',
            method: 'sum',
            fields: ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9'],
            bands: [
                { upTo: 4, label: 'Mínima', severity: 'INFO' },
                { upTo: 9, label: 'Leve', severity: 'LOW' },
                { upTo: 14, label: 'Moderada', severity: 'MEDIUM' },
                { upTo: 19, label: 'Moderadamente severa', severity: 'HIGH' },
                { upTo: 27, label: 'Severa', severity: 'CRITICAL' },
            ],
        },
    ],
    chartMetrics: ['phq9_total'],
    completionMessage: 'Gracias por responder. Si en algún momento sentís que no podés solo, hablá con tu equipo de seguimiento.',
});
export const gad7 = define({
    key: 'gad7',
    name: 'Cuestionario de ansiedad (GAD-7)',
    shortName: 'Ansiedad',
    description: 'Durante las últimas 2 semanas, ¿con qué frecuencia te molestaron estos problemas?',
    icon: 'wind',
    category: 'WELLBEING',
    instrument: {
        name: 'Generalized Anxiety Disorder-7 (GAD-7)',
        source: 'Spitzer, Kroenke, Williams & Löwe — Pfizer',
        license: 'public-domain',
    },
    frequency: 'MONTHLY',
    sections: [
        {
            key: 'gad7',
            fields: [
                likert('q1', 'Sentirte nervioso, ansioso o con los nervios de punta', FREQ_0_3),
                likert('q2', 'No poder parar de preocuparte o controlar la preocupación', FREQ_0_3),
                likert('q3', 'Preocuparte demasiado por distintas cosas', FREQ_0_3),
                likert('q4', 'Dificultad para relajarte', FREQ_0_3),
                likert('q5', 'Estar tan inquieto que te cuesta quedarte quieto', FREQ_0_3),
                likert('q6', 'Irritarte o enojarte con facilidad', FREQ_0_3),
                likert('q7', 'Sentir miedo, como si algo terrible fuera a pasar', FREQ_0_3),
            ],
        },
    ],
    scores: [
        {
            key: 'gad7_total',
            label: 'Puntaje GAD-7',
            method: 'sum',
            fields: ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'],
            bands: [
                { upTo: 4, label: 'Mínima', severity: 'INFO' },
                { upTo: 9, label: 'Leve', severity: 'LOW' },
                { upTo: 14, label: 'Moderada', severity: 'MEDIUM' },
                { upTo: 21, label: 'Severa', severity: 'HIGH' },
            ],
        },
    ],
    chartMetrics: ['gad7_total'],
});
export const distressThermometer = define({
    key: 'distress_nccn',
    name: 'Termómetro de malestar',
    shortName: 'Malestar',
    description: '¿Cuánto malestar sentiste durante la última semana, incluyendo hoy?',
    icon: 'gauge',
    category: 'WELLBEING',
    instrument: {
        name: 'NCCN Distress Thermometer and Problem List',
        source: 'National Comprehensive Cancer Network',
        license: 'free-with-attribution',
    },
    frequency: 'MONTHLY',
    sections: [
        {
            key: 'thermometer',
            fields: [
                {
                    key: 'distress',
                    label: 'Nivel de malestar',
                    type: 'scale',
                    required: true,
                    min: 0,
                    max: 10,
                    step: 1,
                    anchors: { min: 'Nada de malestar', max: 'Malestar extremo' },
                },
            ],
        },
        {
            key: 'problems',
            title: '¿Qué te estuvo generando malestar?',
            fields: [
                {
                    key: 'practical',
                    label: 'Problemas prácticos',
                    type: 'multichoice',
                    options: [
                        { value: 'childcare', label: 'Cuidado de los hijos' },
                        { value: 'housing', label: 'Vivienda' },
                        { value: 'insurance', label: 'Obra social o cobertura' },
                        { value: 'work', label: 'Trabajo o estudio' },
                        { value: 'transport', label: 'Transporte' },
                        { value: 'money', label: 'Dinero' },
                    ],
                },
                {
                    key: 'family',
                    label: 'Problemas familiares',
                    type: 'multichoice',
                    options: [
                        { value: 'partner', label: 'Con mi pareja' },
                        { value: 'children', label: 'Con mis hijos' },
                        { value: 'caregiver', label: 'Con quien me cuida' },
                    ],
                },
                {
                    key: 'emotional',
                    label: 'Problemas emocionales',
                    type: 'multichoice',
                    options: [
                        { value: 'worry', label: 'Preocupación' },
                        { value: 'sadness', label: 'Tristeza' },
                        { value: 'depression', label: 'Depresión' },
                        { value: 'fear', label: 'Miedo' },
                        { value: 'loneliness', label: 'Soledad' },
                        { value: 'anger', label: 'Enojo' },
                        { value: 'loss_interest', label: 'Pérdida de interés' },
                    ],
                },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['distress'],
});
export const sleep = define({
    key: 'sleep',
    name: 'Sueño',
    shortName: 'Sueño',
    icon: 'moon',
    category: 'WELLBEING',
    frequency: 'DAILY',
    sections: [
        {
            key: 'sleep',
            fields: [
                { key: 'hours', label: '¿Cuántas horas dormiste?', type: 'number', required: true, unit: 'hs', min: 0, max: 24, step: 0.5 },
                {
                    key: 'quality',
                    label: '¿Cómo dormiste?',
                    type: 'choice',
                    required: true,
                    options: [
                        { value: 'very_good', label: 'Muy bien', score: 4 },
                        { value: 'good', label: 'Bien', score: 3 },
                        { value: 'fair', label: 'Regular', score: 2 },
                        { value: 'poor', label: 'Mal', score: 1 },
                        { value: 'very_poor', label: 'Muy mal', score: 0 },
                    ],
                },
                {
                    key: 'awakenings',
                    label: '¿Cuántas veces te despertaste?',
                    type: 'number',
                    min: 0,
                    max: 20,
                    step: 1,
                },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['hours', 'quality'],
});
export const activity = define({
    key: 'activity',
    name: 'Actividad física',
    shortName: 'Actividad',
    description: 'Moverte, aunque sea poco, mejora la fatiga y el ánimo durante el tratamiento.',
    icon: 'footprints',
    category: 'WELLBEING',
    frequency: 'DAILY',
    sections: [
        {
            key: 'activity',
            fields: [
                { key: 'minutes', label: 'Minutos de actividad', type: 'number', required: true, unit: 'min', min: 0, max: 600, step: 5 },
                {
                    key: 'kind',
                    label: '¿Qué hiciste?',
                    type: 'choice',
                    options: [
                        { value: 'walk', label: 'Caminar' },
                        { value: 'stretching', label: 'Elongación' },
                        { value: 'strength', label: 'Fuerza' },
                        { value: 'yoga', label: 'Yoga' },
                        { value: 'cycling', label: 'Bicicleta' },
                        { value: 'swimming', label: 'Natación' },
                        { value: 'other', label: 'Otra' },
                    ],
                },
                {
                    key: 'exertion',
                    label: '¿Cuánto esfuerzo te costó?',
                    type: 'scale',
                    min: 0,
                    max: 10,
                    step: 1,
                    anchors: { min: 'Nada de esfuerzo', max: 'Esfuerzo máximo' },
                },
            ],
        },
    ],
    scores: [],
    chartMetrics: ['minutes'],
});
// ── Registro ─────────────────────────────────────────────────────────────────
export const TRACKER_TEMPLATES = [
    esasR,
    proCtcae,
    vitals,
    painDiary,
    weight,
    mealLog,
    hydration,
    medAdherence,
    labValues,
    phq9,
    gad7,
    distressThermometer,
    sleep,
    activity,
];
export const TRACKER_TEMPLATES_BY_KEY = Object.fromEntries(TRACKER_TEMPLATES.map((t) => [t.key, t]));
/** Plantillas que se habilitan por defecto al crear un tenant nuevo. */
export const DEFAULT_ENABLED_TEMPLATES = [
    'esas_r',
    'weight',
    'meal_log',
    'hydration',
    'med_adherence',
    'vitals',
];
export const CATEGORY_LABELS = {
    SYMPTOMS: 'Síntomas',
    NUTRITION: 'Nutrición',
    TREATMENT: 'Tratamiento',
    WELLBEING: 'Bienestar',
};
//# sourceMappingURL=templates.js.map